import { useEffect, useState } from "react";

export type ResolvedTheme = "light" | "dark";

// Every app renders dark mode by putting a `dark` class on <html> (see the
// tokens package), whether the user chose it or it follows the operating
// system. Components that cannot be styled through CSS alone, like the map
// tiles and Sonner's toasts, read that class rather than the OS so they never
// disagree with the page.
function readDocumentTheme(): ResolvedTheme {
  if (typeof document === "undefined") {
    return "light";
  }
  return document.documentElement.classList.contains("dark") ? "dark" : "light";
}

export function useResolvedTheme(themeOverride?: ResolvedTheme): ResolvedTheme {
  const [documentTheme, setDocumentTheme] = useState(readDocumentTheme);

  useEffect(() => {
    if (themeOverride) return;
    // A preference change or an OS switch toggles the class after mount.
    setDocumentTheme(readDocumentTheme());
    const observer = new MutationObserver(() => {
      setDocumentTheme(readDocumentTheme());
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, [themeOverride]);

  return themeOverride ?? documentTheme;
}
